import { useEffect, useState } from 'react';

interface DonutChartProps {
  // Identificador único
  id: string;
  // Valor a exibir no centro
  value: number;
  // Unidade (L, m³, etc)
  meterType: string;
  // Casas decimais
  decimalPlaces?: number;
  // Porcentagem de preenchimento (0-100)
  strokePercent: number;
  // Cor do círculo de progresso
  strokeColor: string;
  // Cor do fundo do círculo
  backgroundColor?: string;
  // Offset do stroke (posição inicial)
  strokeDashoffset?: number;
  // Classes extras no container
  containerClassName?: string;
  // Estilo do container wrapper (ex: display:flex)
  wrapperStyle?: React.CSSProperties;
  // ID do wrapper div (ex: donut-chart, donut-chart2)
  wrapperId?: string;
  // Cor do shadow (padrão: usa strokeColor)
  shadowColor?: string;
}

/**
 * DonutChart - Gráfico circular com valor central
 * 
 * Usado nas páginas de consumo para mostrar consumo atual vs média.
 * O valor central é animado gradualmente junto com o preenchimento do círculo.
 * 
 * Estrutura CSS migrada para Tailwind:
 * - .donut: relative, margin auto, width 123px
 * - .circle-chart: padding 4px, z-index 1
 * - .donut_value: absolute centered flex, font-weight 600, 20px
 * - .chart-shadow: relative com bottom 30px (move para cima), shadow inset
 */
export function DonutChart({
  id,
  value,
  meterType,
  decimalPlaces = 0,
  strokePercent,
  strokeColor,
  backgroundColor = '#eaeef0',
  strokeDashoffset = 0,
  containerClassName = 'donut',
  wrapperStyle,
  wrapperId,
  shadowColor,
}: DonutChartProps) {
  // Estado para animação do valor
  const [displayValue, setDisplayValue] = useState(0);
  
  // Animação do valor de 0 até o valor final
  useEffect(() => {
    const duration = 1000; // 1 segundo, igual à animação do círculo
    const startTime = Date.now();
    const startValue = 0;
    const endValue = value;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing ease-in-out
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const currentValue = startValue + (endValue - startValue) * easeProgress;
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value]);

  const formattedValue = decimalPlaces > 0 
    ? displayValue.toFixed(decimalPlaces) 
    : Math.floor(displayValue).toString();

  // Cor do shadow: usa prop ou fallback para strokeColor
  const finalShadowColor = shadowColor || strokeColor;

  // Estrutura interna do donut - totalmente migrado para Tailwind
  const donutContent = (
    <>
      {/* SVG do círculo */}
      <svg 
        className="circle-chart p-[4px] z-[1] absolute" 
        viewBox="0 0 35 35" 
        width="120" 
        height="120" 
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Círculo de fundo */}
        <circle 
          className="donut_background origin-center" 
          stroke={backgroundColor} 
          strokeWidth="4" 
          fill="none" 
          strokeLinecap="round" 
          strokeDasharray="100,100" 
          cx="17.5" 
          cy="17.5" 
          r="15.5" 
          style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} 
        />
        {/* Círculo de progresso */}
        <circle 
          className="donut_circle_value origin-center transition-all duration-1000 ease-in-out" 
          stroke={strokeColor} 
          strokeWidth="4" 
          strokeDasharray={`${Math.floor(strokePercent)},100`} 
          strokeDashoffset={strokeDashoffset} 
          strokeLinecap="round" 
          fill="none" 
          cx="17.5" 
          cy="17.5" 
          r="15.5" 
          style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }} 
        />
      </svg>
      
      {/* Shadow - position relative com bottom move para cima */}
      <div 
        className="
          chart-shadow
          relative
          w-[100px] h-[111px]
          rounded-full
          z-0
          bottom-[30px]
          animate-fadeIn
        "
        style={{ 
          boxShadow: `inset 26px 9px 40px -21px ${finalShadowColor}`,
          top: '5px',
          left: '7px',
        }}
      ></div>
      
      {/* Valor central */}
      <div 
        className="
          donut_value
          absolute top-0
          flex justify-center items-center
          w-[120px] h-[120px]
          font-semibold text-[20px]
          font-['Poppins',sans-serif]
        " 
        id={id} 
        data-value={value} 
        data-decimal-places={decimalPlaces}
      >
        {formattedValue} {meterType}
      </div>
    </>
  );

  // Donut com wrapper (donut 1 e 2)
  if (wrapperStyle) {
    return (
      <div style={wrapperStyle} id={wrapperId}>
        <div className={`${containerClassName} relative mx-auto my-[5px] w-[123px]`}>
          {donutContent}
        </div>
      </div>
    );
  }

  // Donut sem wrapper (donut 3) - alinhado pelo layout flex do pai
  return (
    <div className={`${containerClassName} relative my-[5px] w-[123px]`} id={wrapperId}>
      {donutContent}
    </div>
  );
}
